import React, { useEffect, useState } from "react";
import logo from "../../assets/svg/auth/logo.svg";
import switchDesktopIcon from "../../assets/svg/free trial/switch-desktop.svg";
import { buttonClasses } from "../ui/Button";
import { EXTERNAL_LINKS } from "../../routes/paths";

/**
 * Narrowest viewport the app is allowed to run at. 1024px is the usual desktop
 * cut: it clears phones in either orientation and tablets held in portrait,
 * and it sits above the `md` (768px) point where the shell swaps its drawer
 * for the static sidebar rail — so nothing below this ever renders again.
 */
export const DESKTOP_MIN_WIDTH = 1024;

const QUERY = `(min-width: ${DESKTOP_MIN_WIDTH}px)`;

/**
 * True while the viewport is at least `DESKTOP_MIN_WIDTH` wide, kept current
 * as the window is resized or the device is turned.
 *
 * Environments without `matchMedia` — jsdom under the test runner — count as
 * desktop, so tests that mount a screen are not answering a size question the
 * browser is the only one that can answer.
 */
const useIsDesktop = (): boolean => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? true
      : window.matchMedia(QUERY).matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;

    const list = window.matchMedia(QUERY);
    const update = () => setIsDesktop(list.matches);

    // Re-read on mount as well: the width can have changed between the state
    // initialiser above and this effect.
    update();

    // Safari only gained `addEventListener` on MediaQueryList in 14, and the
    // deprecated `addListener` is all that is there before it.
    if (typeof list.addEventListener === "function") {
      list.addEventListener("change", update);
      return () => list.removeEventListener("change", update);
    }
    list.addListener(update);
    return () => list.removeListener(update);
  }, []);

  return isDesktop;
};

/** The screen shown in place of the app on anything narrower than a desktop. */
const DesktopOnlyNotice: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FCFCFD] px-6 py-10">
    <div className="max-w-[360px] text-center">
      <img src={logo} alt="Naurist" className="h-7 w-auto mx-auto" />

      <img
        src={switchDesktopIcon}
        alt=""
        aria-hidden="true"
        className="mt-24 mx-auto h-[62px] w-[62px]"
      />

      <h1 className="mt-6 text-[22px] font-semibold text-ink">
        Switch to desktop
      </h1>
      <p className="mt-1 text-[15px] font-medium text-brand">
        To activate free trial.
      </p>
      <p className="mt-4 text-[14px] leading-6 text-ink-muted">
        Naurist is designed for desktop to deliver the best experience,
        making it easier to manage everything in one place.
      </p>

      {/*
        An anchor rather than a button: there is no app left to act on down
        here, and the only thing left to offer is the marketing site. It wears
        the primary button's look so it still reads as the screen's action.
      */}
      <a
        href={EXTERNAL_LINKS.home}
        className={buttonClasses({
          variant: "primary",
          size: "lg",
          className: "mt-8 px-10",
        })}
      >
        Okay, thanks
      </a>
    </div>
  </div>
);

/**
 * Gate that keeps the app to desktop viewports.
 *
 * The children are not rendered at all below the cut, rather than hidden with
 * CSS: nothing mounts, so no screen behind the notice fetches, redirects, or
 * pins the body's scroll for a layout the user will never see.
 */
const DesktopOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isDesktop = useIsDesktop();

  return isDesktop ? <>{children}</> : <DesktopOnlyNotice />;
};

export default DesktopOnly;
